import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { tpoGenUrls } from 'config/urls';
import { formatPeriodLabel } from '../../../../utils/formatPeriodLabel';
import { PAGE_LIMIT } from '../../config/metricConfig';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { mergeAttributeFilters } from '../../utils/mergeAttributeFilters';
import type {
    AttributeOption,
    FiltersPayload,
    Granularity,
    PeriodColumn,
    PlanMetricChartBounds,
    PlanMetricDimensionRow,
    PlanMetricRequest,
    PlanMetricResponse,
    PlanMetricRow,
    SortOrder,
    SubMode,
    TimePeriod,
} from '../../types';

interface UsePlanMetricDataParams {
    planId: string;
    subMode: SubMode;
    dimension: string;
    attributes: AttributeOption[];
    kpis: string[];
    granularity: Granularity;
    timePeriod: TimePeriod;
    searchKeyword: string;
    sharedFilters: FiltersPayload;
    sortBy: string;
    sortOrder: SortOrder;
}

interface UsePlanMetricDataResult {
    dataSource: PlanMetricRow[];
    periods: PeriodColumn[];
    chartBounds: PlanMetricChartBounds;
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    totalCount: number;
    loadedCount: number;
    error: unknown;
}

const fetchPlanMetricData = async (
    planId: string,
    body: PlanMetricRequest,
    signal: AbortSignal,
): Promise<PlanMetricResponse> => {
    let token = '';
    try {
        token = localStorage.getItem('accessToken') ?? '';
    } catch {
        // incognito / disabled storage; fall through with empty token
    }
    const response = await axios(tpoGenUrls.getPeriodReportData(planId), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
        data: body,
    });
    return response.data;
};

const usePlanMetricData = (params: UsePlanMetricDataParams): UsePlanMetricDataResult => {
    const {
        planId,
        subMode,
        dimension,
        attributes,
        kpis,
        granularity,
        timePeriod,
        searchKeyword,
        sharedFilters,
        sortBy,
        sortOrder,
    } = params;

    const { metricPageConfig } = useTpoGenConfig();

    const hasInclusion =
        attributes.length > 0 ||
        Object.values(sharedFilters ?? {}).some((d) => (d?.inclusion?.length ?? 0) > 0);
    const enabled =
        !!planId &&
        kpis.length > 0 &&
        (subMode === 'dimension' ? !!dimension : hasInclusion);

    const filters = useMemo(() => {
        if (subMode !== 'metric') return sharedFilters ?? {};
        const dimKey = metricPageConfig.dimensionKeyMap[dimension] ?? null;
        return mergeAttributeFilters(sharedFilters ?? {}, dimKey, attributes);
    }, [subMode, dimension, attributes, sharedFilters, metricPageConfig]);

    const { data, isPending, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, error } = useInfiniteQuery({
        queryKey: [
            'planMetricData',
            planId,
            subMode,
            dimension,
            kpis,
            granularity,
            timePeriod,
            searchKeyword,
            filters,
            sortBy,
            sortOrder,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchPlanMetricData(
                planId,
                {
                    view_type: subMode,
                    kpis,
                    granularity,
                    time_period: timePeriod,
                    dimension: subMode === 'dimension' ? dimension : undefined,
                    filters,
                    search_string: searchKeyword,
                    sort_by: sortBy,
                    sorting_order: sortOrder,
                    limit: PAGE_LIMIT,
                    offset: pageParam,
                },
                signal,
            ),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const totalCount = lastPage?.metadata?.total_count ?? 0;
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return nextOffset < totalCount ? nextOffset : undefined;
        },
        enabled,
        // Overrides the app-wide 1hr staleTime/gcTime so this report always
        // hits the network instead of serving a cached response.
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: 'always',
    });

    const merged = useMemo(() => {
        if (!data?.pages?.length) {
            return {
                dataSource: [] as PlanMetricRow[],
                periods: [] as PeriodColumn[],
                chartBounds: { min: 0, max: 0 } as PlanMetricChartBounds,
            };
        }
        const periods = (data.pages[0].periods ?? []).map((p: PeriodColumn) => ({
            ...p,
            label: formatPeriodLabel(p.period_start_date, granularity),
        }));
        const dataSource = data.pages.flatMap((page) => page.tableData ?? []);

        // chartBounds: only meaningful for dimension sub-mode where every row
        // shares one KPI's unit. Computed once here so the trend chart's Y-axis
        // stays stable as the user checks / unchecks rows.
        let min = Number.POSITIVE_INFINITY;
        let max = Number.NEGATIVE_INFINITY;
        if (subMode === 'dimension') {
            for (const row of dataSource as PlanMetricDimensionRow[]) {
                for (const p of periods) {
                    const v = row[p.key];
                    if (typeof v === 'number') {
                        if (v < min) min = v;
                        if (v > max) max = v;
                    }
                }
            }
        }
        const chartBounds: PlanMetricChartBounds = {
            min: Number.isFinite(min) ? min : 0,
            max: Number.isFinite(max) ? max : 0,
        };

        return { dataSource, periods, chartBounds };
    }, [data, subMode, granularity]);

    const totalCount = data?.pages?.[0]?.metadata?.total_count ?? 0;
    const loadedCount = merged.dataSource.length;

    return {
        dataSource: merged.dataSource,
        periods: merged.periods,
        chartBounds: merged.chartBounds,
        isPending: enabled ? isPending : false,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage: () => fetchNextPage(),
        totalCount,
        loadedCount,
        error,
    };
};

export default usePlanMetricData;
